import { appointmentTypeKey } from '@/utils/appointmentFrequency';
import formatDate from '@/utils/formatDate';
import { STATUS_META } from '@/utils/taskConstants';

import { DeviceVendor, getVendorDisplayName } from '@/types/matchDevice';
import { TaskBusinessState } from '@/types/task';
import { UserRole } from '@/types/user';

/** Internal roles. Clinic users reach the same page through canViewSchedule. */
const SCHEDULE_PAGE_ROLES = new Set<string>([
  UserRole.InternalSuperAdmin,
  UserRole.InternalAdmin,
  UserRole.InternalClinical,
  UserRole.InternalNonClinical
]);

export function canViewInternalSchedule(role?: string | null): boolean {
  return Boolean(role && SCHEDULE_PAGE_ROLES.has(role));
}

const EXTERNAL_SCHEDULE_ROLES = new Set<string>([
  UserRole.ExternalAdmin,
  UserRole.ExternalPractitionerAdmin,
  UserRole.ExternalPractitioner,
  UserRole.ExternalProvider,
  UserRole.ExternalNonPractitionerAdmin,
  UserRole.ExternalNonPractitioner,
  UserRole.ExternalNonProvider,
  UserRole.ExternalClerical,
  UserRole.ExternalHeartFailure
]);

/** Clinic users can open the schedule. Internal-only columns and actions stay separate. */
export function canViewSchedule(role?: string | null): boolean {
  return canViewInternalSchedule(role) || Boolean(role && EXTERNAL_SCHEDULE_ROLES.has(role));
}

export function isExternalScheduleUser(role?: string | null): boolean {
  return Boolean(role && EXTERNAL_SCHEDULE_ROLES.has(role));
}

/** A day cell lists people until this many appointments, then it shows counts. */
export const CALENDAR_INDIVIDUAL_LIMIT = 4;

export function calendarDayCounts(
  rows: ScheduleListRow[],
  groupBy: 'status' | 'type'
): { key: string; label: string; color: string; count: number }[] {
  const counts = new Map<string, { key: string; label: string; color: string; count: number }>();
  rows.forEach((row) => {
    const meta = groupBy === 'status' ? scheduleStatusMeta(row.status) : scheduleTypeMeta(row.type);
    const current = counts.get(meta.label);
    if (current) current.count += 1;
    else
      counts.set(meta.label, { key: meta.value, label: meta.label, color: meta.color, count: 1 });
  });
  return [...counts.values()];
}

export const SCHEDULE_PAGE_LIMIT = 100;

/** The appointments list rejects a page larger than 200. */
export const CALENDAR_QUERY_LIMIT = 200;
export const CALENDAR_MAX_ROWS = 12000;

export const SCHEDULE_STATUSES: { value: string; label: string; color: string }[] = [
  { value: 'scheduled', label: 'Scheduled', color: 'blue' },
  { value: 'pending', label: 'Pending', color: 'orange' },
  { value: 'completed', label: 'Completed', color: 'teal' },
  { value: 'no show', label: 'No Show', color: 'red' },
  { value: 'missed', label: 'Missed', color: 'grape' },
  { value: 'cancelled', label: 'Cancelled', color: 'gray' }
];

export const SCHEDULE_TYPES: { value: string; label: string; color: string }[] = [
  { value: 'routine', label: 'Routine', color: 'teal' },
  { value: 'heart failure', label: 'Heart Failure', color: 'pink' },
  { value: 'combo', label: 'Combo', color: 'violet' },
  { value: 'eri tracking', label: 'ERI Tracking', color: 'yellow' },
  { value: 'physician request', label: 'Physician Request', color: 'indigo' },
  { value: 'in clinic', label: 'In Clinic', color: 'cyan' },
  { value: 'ad hoc', label: 'Non-routine', color: 'gray' }
];

export const SCHEDULE_COLUMNS = [
  { id: 'patient', label: 'Patient', pinned: true },
  { id: 'actions', label: 'Actions', pinned: true },
  { id: 'clinic', label: 'Clinic', pinned: false },
  { id: 'date', label: 'Scheduled date', pinned: false },
  { id: 'createdAt', label: 'Created', pinned: false },
  { id: 'updatedAt', label: 'Modified', pinned: false },
  { id: 'type', label: 'Type', pinned: false },
  { id: 'device', label: 'Device', pinned: false },
  { id: 'vendor', label: 'Vendor', pinned: false },
  { id: 'status', label: 'Status', pinned: false },
  { id: 'eri', label: 'ERI', pinned: false },
  { id: 'frequency', label: 'Frequency', pinned: false },
  { id: 'uuid', label: 'UUID', pinned: false },
  { id: 'salesforceId', label: 'Salesforce ID', pinned: false },
  { id: 'csn', label: 'CSN', pinned: false },
  { id: 'encounterId', label: 'Encounter ID', pinned: false }
] as const;

export type ScheduleColumnId = (typeof SCHEDULE_COLUMNS)[number]['id'];

export const DEFAULT_HIDDEN_SCHEDULE_COLUMNS: ScheduleColumnId[] = [
  'vendor',
  'salesforceId',
  'csn',
  'encounterId'
];

/** Clinic users never see these, even if a saved preference turns them on. */
export const EXTERNAL_EXCLUDED_SCHEDULE_COLUMNS: ScheduleColumnId[] = [
  'clinic',
  'uuid',
  'salesforceId'
];

/** Device on the appointment, once the list payload includes it. */
export interface ScheduleLinkedDevice {
  id: string;
  vendor: string;
  type: string;
  modelName: string;
  modelNumber: string;
  serialNumber: string;
  isEriTracked: boolean | null;
}

/** Same related-task columns as the task page. */
export const SCHEDULE_TASK_LINKS = [
  { id: 'transmissionTask', label: 'Transmission', code: 'transmission_tracking' },
  { id: 'checkInTask', label: 'Check-In', code: 'check_in' },
  { id: 'reportUploadTask', label: 'Report upload', code: 'report_upload' },
  { id: 'uploadEncounterTask', label: 'Upload + encounter', code: 'report_upload_with_encounter' }
] as const;

export type ScheduleTaskLinkId = (typeof SCHEDULE_TASK_LINKS)[number]['id'];
export type ScheduleTaskCode = (typeof SCHEDULE_TASK_LINKS)[number]['code'];

export interface ScheduleLinkedTask {
  id: string;
  code: ScheduleTaskCode;
  taskNumber: string;
  status: string;
}

export interface ScheduleListRow {
  id: string;
  patientId: string;
  patientName: string;
  clinicId: string;
  patientBirthDate: string;
  patientMrn: string;
  date: string;
  createdAt: string;
  updatedAt: string;
  type: string;
  status: string;
  deviceType: string;
  deviceVendor: string;
  /** Set when the appointment payload includes a device type or vendor. */
  linkedDevice: ScheduleLinkedDevice | null;
  /** Related tasks, once the appointment payload includes them. */
  linkedTasks: Partial<Record<ScheduleTaskCode, ScheduleLinkedTask>>;
  frequency: number | null;
  includeEriTracking: boolean;
  reportId: string;
  matchDeviceId: string;
  salesforceRemoteId: string;
  csn: string;
  encounterId: string;
}

export function scheduleStatusMeta(status: string) {
  const key = appointmentTypeKey(status);
  const normalized = key === 'canceled' ? 'cancelled' : key;
  return (
    SCHEDULE_STATUSES.find((item) => item.value === normalized) ?? {
      value: normalized,
      label: normalized.replace(/\b\w/g, (letter) => letter.toUpperCase()),
      color: 'gray'
    }
  );
}

export function scheduleTypeMeta(type: string) {
  const key = appointmentTypeKey(type);
  return (
    SCHEDULE_TYPES.find((item) => item.value === key) ?? {
      value: key,
      label: key.replace(/\b\w/g, (letter) => letter.toUpperCase()),
      color: 'gray'
    }
  );
}

export function toApiDay(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

export function startOfDay(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

/** Today through six days ahead. A short window keeps the list query small. */
export function defaultScheduleRange(today = new Date()): [Date, Date] {
  const start = startOfDay(today);
  const end = new Date(start);
  end.setDate(end.getDate() + 6);
  return [start, end];
}

export function monthScheduleRange(today = new Date()): [Date, Date] {
  const start = new Date(today.getFullYear(), today.getMonth(), 1);
  const end = new Date(today.getFullYear(), today.getMonth() + 1, 0);
  return [start, end];
}

export function effectiveScheduleClinicIds(
  accessibleClinicIds: readonly string[],
  selectedClinicIds: readonly string[]
): string[] {
  const accessible = [...new Set(accessibleClinicIds.filter(Boolean))];
  if (selectedClinicIds.length === 0) return accessible;
  const selected = new Set(selectedClinicIds.filter(Boolean));
  return accessible.filter((clinicId) => selected.has(clinicId));
}

export function buildScheduleQueryArgs(input: {
  start: Date | null;
  end: Date | null;
  page: number;
  statuses: string[];
  types: string[];
  accessibleClinicIds: readonly string[];
  selectedClinicIds: readonly string[];
  layout: 'list' | 'calendar';
  sortColumn: 'date' | 'frequency';
  sortDirection: 'asc' | 'desc';
  pageSize: number;
}): ReturnType<typeof scheduleQueryParams> | null {
  if (!input.start || !input.end) return null;
  const effectiveClinicIds = effectiveScheduleClinicIds(
    input.accessibleClinicIds,
    input.selectedClinicIds
  );
  if (effectiveClinicIds.length === 0) return null;
  return scheduleQueryParams({
    start: input.start,
    end: input.end,
    page: input.layout === 'calendar' ? 1 : input.page,
    statuses: input.statuses,
    types: input.types,
    organizationIds: effectiveClinicIds,
    sortOrder: input.sortColumn === 'date' ? input.sortDirection : 'asc',
    limit: input.layout === 'calendar' ? CALENDAR_QUERY_LIMIT : input.pageSize
  });
}

export function scheduleQueryParams(input: {
  start: Date;
  end: Date;
  page: number;
  statuses: string[];
  types: string[];
  organizationIds?: string[];
  sortOrder?: 'asc' | 'desc';
  limit?: number;
}): {
  startDate: string;
  endDate: string;
  page: number;
  limit: number;
  statuses?: string;
  type?: string;
  organizationIds?: string;
  sortOrder?: 'asc' | 'desc';
} | null {
  const start = input.start.getTime() <= input.end.getTime() ? input.start : input.end;
  const end = input.start.getTime() <= input.end.getTime() ? input.end : input.start;
  const type = input.types.length === 1 ? input.types[0] : undefined;
  const organizationIds = (input.organizationIds ?? []).filter(Boolean);
  if (input.organizationIds !== undefined && organizationIds.length === 0) {
    return null;
  }
  return {
    startDate: toApiDay(start),
    endDate: toApiDay(end),
    page: input.page,
    limit: input.limit ?? SCHEDULE_PAGE_LIMIT,
    ...(input.statuses.length > 0 ? { statuses: input.statuses.join(',') } : {}),
    ...(type ? { type } : {}),
    ...(organizationIds.length > 0 ? { organizationIds: organizationIds.join(',') } : {}),
    ...(input.sortOrder === 'desc' ? { sortOrder: 'desc' as const } : {})
  };
}

/** Date sort is the appointment list's sortOrder. Frequency sorts the rows already loaded. */
export function sortScheduleRows(
  rows: ScheduleListRow[],
  sort: { column: 'date' | 'frequency'; direction: 'asc' | 'desc' }
): ScheduleListRow[] {
  if (sort.column !== 'frequency') return rows;
  const factor = sort.direction === 'asc' ? 1 : -1;
  return [...rows].sort((left, right) => {
    if (left.frequency == null) return right.frequency == null ? 0 : 1;
    if (right.frequency == null) return -1;
    return (left.frequency - right.frequency) * factor;
  });
}

function asRecord(value: unknown): Record<string, unknown> | null {
  if (!value || typeof value !== 'object') return null;
  return value as Record<string, unknown>;
}

function asText(value: unknown): string {
  return typeof value === 'string' ? value.trim() : '';
}

function asFrequency(value: unknown): number | null {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string' && value.trim() !== '' && Number.isFinite(Number(value))) {
    return Number(value);
  }
  return null;
}

function linkedDeviceFrom(
  row: Record<string, unknown>,
  device: Record<string, unknown> | null
): ScheduleLinkedDevice | null {
  const type = asText(device?.type) || asText(row.deviceType);
  const vendor = asText(device?.vendor) || asText(row.deviceVendor);
  if (!type && !vendor) return null;
  return {
    id: asText(row.matchDeviceId) || asText(device?.id),
    vendor,
    type,
    modelName: asText(device?.modelName),
    modelNumber: asText(device?.modelNumber),
    serialNumber: asText(device?.serialNumber),
    isEriTracked: typeof device?.isEriTracked === 'boolean' ? device.isEriTracked : null
  };
}

const TASK_CODE_SET = new Set<string>(SCHEDULE_TASK_LINKS.map((column) => column.code));

function asTaskCode(value: string): ScheduleTaskCode | null {
  return TASK_CODE_SET.has(value) ? (value as ScheduleTaskCode) : null;
}

function asLinkedTask(value: unknown, fallbackCode = ''): ScheduleLinkedTask | null {
  const row = asRecord(value);
  if (!row) return null;
  const type = asRecord(row.taskType);
  const code = asTaskCode(
    asText(row.code) || asText(row.taskTypeCode) || asText(type?.code) || fallbackCode
  );
  const id = asText(row.id) || asText(row.taskId);
  if (!code || !id) return null;
  return {
    id,
    code,
    taskNumber: asText(row.taskNumber) || asText(row.number),
    status: asText(row.businessState) || asText(row.status)
  };
}

function linkedTasksFrom(
  row: Record<string, unknown>
): Partial<Record<ScheduleTaskCode, ScheduleLinkedTask>> {
  const linked: Partial<Record<ScheduleTaskCode, ScheduleLinkedTask>> = {};
  const remember = (task: ScheduleLinkedTask | null) => {
    if (task) linked[task.code] = task;
  };
  for (const key of ['tasks', 'linkedTasks', 'relatedTasks'] as const) {
    const list = row[key];
    if (!Array.isArray(list)) continue;
    list.forEach((item) => remember(asLinkedTask(item)));
  }
  remember(asLinkedTask(row.task));
  SCHEDULE_TASK_LINKS.forEach((column) => {
    remember(asLinkedTask(row[column.id], column.code));
  });
  return linked;
}

/** Reads the list payload, including task and device links when the API sends them. */
export function scheduleRowFromApi(raw: unknown): ScheduleListRow | null {
  const row = asRecord(raw);
  if (!row) return null;
  const id = asText(row.id);
  if (!id) return null;

  const patient = asRecord(row.patient);
  const device = asRecord(row.matchDevice) ?? asRecord(row.device);
  const linkedDevice = linkedDeviceFrom(row, device);
  const given = asText(patient?.givenName);
  const family = asText(patient?.familyName);
  const name = asText(patient?.name) || [given, family].filter(Boolean).join(' ');
  const visit = asRecord(row.ehrVisit);
  const report = row.linkedTransmissionReportId ?? row.transmissionReportId ?? row.reportId;
  const reportId = report === null || report === undefined || report === '' ? '' : String(report);

  return {
    id,
    patientId: asText(row.patientId) || asText(patient?.id),
    patientName: name,
    clinicId: asText(row.organizationId) || asText(row.clinicId),
    patientBirthDate:
      asText(patient?.birthDate) || asText(patient?.dateOfBirth) || asText(patient?.dob),
    patientMrn: asText(patient?.mrn2),
    date: asText(row.scheduledDate) || asText(row.date),
    createdAt: asText(row.createdAt),
    updatedAt: asText(row.updatedAt),
    type: appointmentTypeKey(asText(row.type)),
    status: appointmentTypeKey(asText(row.status)),
    deviceType: linkedDevice?.type ?? '',
    deviceVendor: linkedDevice?.vendor ?? '',
    linkedDevice,
    linkedTasks: linkedTasksFrom(row),
    frequency: asFrequency(row.frequency ?? row.frequencyNumber),
    includeEriTracking: row.includeEriTracking === true,
    reportId,
    matchDeviceId: asText(row.matchDeviceId) || asText(device?.id),
    salesforceRemoteId: asText(row.salesforceRemoteId),
    csn: asText(row.emrAppointmentId) || asText(row.visitNumber) || asText(visit?.visitNumber),
    encounterId: asText(row.emrEncounterId)
  };
}

export interface ScheduleLoadedFilter {
  search: string;
  types: string[];
  deviceTypes: string[];
  applyTypeFilter: boolean;
  /** Keep rows whose CSN is empty. */
  blankCsn?: boolean;
  /** Keep rows whose encounter id is empty. */
  blankEncounterId?: boolean;
  /** One value narrows to that ERI state. Both or neither leave ERI alone. */
  eri?: Array<'true' | 'false'>;
  /** Empty means every frequency. Values are the cycle days already on the row. */
  frequencies?: number[];
}

function eriNarrowsTo(eri: Array<'true' | 'false'> | undefined): 'true' | 'false' | null {
  if (!eri || eri.length !== 1) return null;
  return eri[0];
}

/** Search, device type, extra types, and the local field filters run on rows already loaded. */
export function schedulePageFiltersActive(input: {
  search: string;
  types: string[];
  deviceTypes: string[];
  blankCsn?: boolean;
  blankEncounterId?: boolean;
  eri?: Array<'true' | 'false'>;
  frequencies?: number[];
}): boolean {
  return (
    Boolean(input.search.trim()) ||
    input.deviceTypes.length > 0 ||
    input.types.length > 1 ||
    Boolean(input.blankCsn) ||
    Boolean(input.blankEncounterId) ||
    eriNarrowsTo(input.eri) !== null ||
    (input.frequencies?.length ?? 0) > 0
  );
}

export function filterScheduleRows(
  rows: ScheduleListRow[],
  input: ScheduleLoadedFilter
): ScheduleListRow[] {
  const query = input.search.trim().toLowerCase();
  const eri = eriNarrowsTo(input.eri);
  const frequencies = input.frequencies ?? [];
  return rows.filter((row) => {
    if (input.applyTypeFilter && input.types.length > 0 && !input.types.includes(row.type)) {
      return false;
    }
    if (input.deviceTypes.length > 0 && !input.deviceTypes.includes(row.deviceType)) return false;
    if (input.blankCsn && row.csn.trim() !== '') return false;
    if (input.blankEncounterId && row.encounterId.trim() !== '') return false;
    if (eri === 'true' && !row.includeEriTracking) return false;
    if (eri === 'false' && row.includeEriTracking) return false;
    if (
      frequencies.length > 0 &&
      (row.frequency === null || !frequencies.includes(row.frequency))
    ) {
      return false;
    }
    if (!query) return true;
    return (
      row.patientName.toLowerCase().includes(query) || row.patientMrn.toLowerCase().includes(query)
    );
  });
}

export function visibleScheduleColumns(hidden: readonly string[]): ScheduleColumnId[] {
  return SCHEDULE_COLUMNS.filter((column) => column.pinned || !hidden.includes(column.id)).map(
    (column) => column.id
  );
}

function scheduleVendorLabel(vendor: string): string {
  if (!vendor) return '';
  if ((Object.values(DeviceVendor) as string[]).includes(vendor)) {
    return getVendorDisplayName(vendor as DeviceVendor);
  }
  return vendor;
}

function csvField(value: string): string {
  const text = /^[=+\-@]/.test(value) ? `'${value}` : value;
  if (/[",\n\r]/.test(text)) return `"${text.replace(/"/g, '""')}"`;
  return text;
}

function calendarCsvDate(value: string): string {
  return value ? formatDate(value, 'MM/DD/YYYY') : '';
}

function timestampCsv(value: string): string {
  return value ? formatDate(value, 'MM/DD/YYYY h:mm A') : '';
}

function taskCsvValue(row: ScheduleListRow, column: ScheduleTaskLinkId): string {
  const code = SCHEDULE_TASK_LINKS.find((item) => item.id === column)?.code;
  const task = code ? row.linkedTasks[code] : undefined;
  if (!task) return '';
  const status = STATUS_META[task.status as TaskBusinessState]?.label ?? task.status;
  return [task.taskNumber || 'Open', status].filter(Boolean).join(' ');
}

function headersFor(column: ScheduleColumnId | ScheduleTaskLinkId): string[] | null {
  if (column === 'actions') return null;
  if (column === 'patient') return ['Patient', 'DOB', 'MRN'];
  const columnLabel = SCHEDULE_COLUMNS.find((item) => item.id === column)?.label;
  if (columnLabel) return [columnLabel];
  const taskLabel = SCHEDULE_TASK_LINKS.find((item) => item.id === column)?.label;
  return taskLabel ? [taskLabel] : null;
}

function cellsFor(
  column: ScheduleColumnId | ScheduleTaskLinkId,
  row: ScheduleListRow,
  clinicName: (clinicId: string) => string
): string[] {
  if (column === 'patient') {
    return [row.patientName, calendarCsvDate(row.patientBirthDate), row.patientMrn];
  }
  if (column === 'clinic') return [clinicName(row.clinicId)];
  if (column === 'date') return [calendarCsvDate(row.date)];
  if (column === 'createdAt') return [timestampCsv(row.createdAt)];
  if (column === 'updatedAt') return [timestampCsv(row.updatedAt)];
  if (column === 'type') return [scheduleTypeMeta(row.type).label];
  if (column === 'device') {
    const vendor = scheduleVendorLabel(row.linkedDevice?.vendor || row.deviceVendor);
    const type = row.linkedDevice?.type || row.deviceType;
    return [[vendor, type].filter(Boolean).join(' ')];
  }
  if (column === 'vendor') return [scheduleVendorLabel(row.deviceVendor)];
  if (column === 'status') return [scheduleStatusMeta(row.status).label];
  if (column === 'eri') return [row.includeEriTracking ? 'On' : 'Off'];
  if (column === 'frequency') return [row.frequency === null ? '' : String(row.frequency)];
  if (column === 'uuid') return [row.id];
  if (column === 'salesforceId') return [row.salesforceRemoteId];
  if (column === 'csn') return [row.csn];
  if (column === 'encounterId') return [row.encounterId];
  if (
    column === 'transmissionTask' ||
    column === 'checkInTask' ||
    column === 'reportUploadTask' ||
    column === 'uploadEncounterTask'
  ) {
    return [taskCsvValue(row, column)];
  }
  return [];
}

/** Spreadsheet of the columns passed in. Actions are left out. Patient splits into name, DOB, and MRN. */
export function scheduleRowsToCsv(
  rows: readonly ScheduleListRow[],
  columns: readonly (ScheduleColumnId | ScheduleTaskLinkId)[],
  clinicName: (clinicId: string) => string
): string {
  const plans = columns
    .map((column) => ({ column, headers: headersFor(column) }))
    .filter((plan): plan is { column: ScheduleColumnId | ScheduleTaskLinkId; headers: string[] } =>
      Boolean(plan.headers)
    );
  const header = plans
    .flatMap((plan) => plan.headers)
    .map(csvField)
    .join(',');
  const lines = rows.map((row) =>
    plans
      .flatMap((plan) => cellsFor(plan.column, row, clinicName))
      .map(csvField)
      .join(',')
  );
  return [header, ...lines].join('\r\n');
}
